import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { DateTime } from 'luxon';
import { getMyApp } from 'app/app-interface';
import { AppVersion } from 'app/version';
import { CsPage, NavController } from 'providers/nav-controller';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { LoggingService, isTransientFetchError, errorReportFields } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { TextDbService } from 'services/text-db.service';
import { ACTIVE_TEXT_SEGMENT, ENGLISH_TEXT_SEGMENT } from 'services/text-locale';
import { CsContentComponent } from 'components/cs-content/cs-content';
import { ChangelogSection, filterAndMarkSections, parseChangelog } from './changelog-sections';

import { LL } from 'i18n/i18n.service';
import { formatRelativeDay } from 'util/formatting';

@Component({
    selector: 'page-changelog',
    templateUrl: 'changelog.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangelogPage extends CsPage<{}> {
  protected LL = LL;

  @ViewChild(CsContentComponent) csContent: CsContentComponent;
  @ViewChild('bottomSentinel', { read: ElementRef }) bottomSentinel: ElementRef<HTMLElement>;

  // all parsed sections (not rendered initially)
  private allSections: ChangelogSection[] = [];

  // visible sections (progressive loading)
  sections: ChangelogSection[] = [];
  canLoadMore = false;

  private readonly INITIAL_BATCH = 8;
  private readonly BATCH_SIZE = 4;
  private isLoadingMore = false;
  private intersectionObserver: IntersectionObserver;
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private textDb: TextDbService,
    private cd: ChangeDetectorRef,
    private logging: LoggingService
  ) {
    super();
  }

  async ionViewCanEnter() {
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    let changelog = await this.textDb.getTextByHelpIdAsync('app-version-changelog');
    if (!changelog) {
      console.error("Can't load changelog source file.");
    }

    if (changelog) {
      this.allSections = filterAndMarkSections(parseChangelog(changelog.text), AppVersion);

      // load initial batch for fast page render
      this.sections = this.allSections.slice(0, this.INITIAL_BATCH);
      this.canLoadMore = this.allSections.length > this.INITIAL_BATCH;

      // the text above is shown in the user's language, but the revision we
      // record as "seen" must come from the ENGLISH document — see
      // design-docs/global-common-localization.md "The What's new gate must
      // stay locale-independent". if the reader is already on English the
      // document just loaded IS that document, so skip the second read.
      let englishChangelog = ACTIVE_TEXT_SEGMENT === ENGLISH_TEXT_SEGMENT
        ? changelog
        : await this.textDb.getTextByHelpIdAsync(
          'app-version-changelog', undefined, ENGLISH_TEXT_SEGMENT);
      await this.markChangelogSeenAsync(englishChangelog._rev);
      getMyApp().showChangelogPageNotification = false;
      this.cd.markForCheck();
    }
  }

  /**
   * Records the changelog revision as seen in the person's profile.
   *
   * If the profile could not be read or written, nothing is saved (df-9241e82523c2): a
   * profile we could not read must not be replaced by a stand-in. The "what's new" card may
   * then come back on a later load, which is the harmless side to err on. A network drop or
   * the database being torn down is expected; anything else is reported.
   */
  private async markChangelogSeenAsync(rev: string): Promise<void> {
    try {
      let profile = await this.userDb.loadProfileStateAsync(this.docWatcher);
      if (profile.lastAppChangelog !== rev) {
        profile.lastAppChangelog = rev;
        await this.userDb.saveProfileStateAsync(profile, DatabaseWrapper.getTransactionId());
      }
    } catch (e) {
      if (isTransientFetchError(e) || isDatabaseTornDown(e)) return;
      this.logging.trackSilentProblem('ChangelogMarkSeenFailed', errorReportFields(e));
    }
  }

  async ionViewDidEnter() {
    this.setupIntersectionObserver();
  }

  async ionViewWillLeave() {
    this.cleanupObserver();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  private setupIntersectionObserver() {
    if (!this.canLoadMore) return;

    const scrollElement = this.csContent?.getScrollRootElement() ?? null;

    const options = {
      root: scrollElement,
      rootMargin: '200px',
      threshold: 0
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoadingMore && this.canLoadMore) {
          this.loadMoreSections();
        }
      });
    }, options);

    if (this.bottomSentinel?.nativeElement) {
      this.intersectionObserver.observe(this.bottomSentinel.nativeElement);
    }
  }

  private loadMoreSections() {
    if (this.viewDestroyed || this.isLoadingMore) return;

    this.isLoadingMore = true;
    const currentLength = this.sections.length;
    const nextBatch = this.allSections.slice(currentLength, currentLength + this.BATCH_SIZE);

    this.sections = [...this.sections, ...nextBatch];
    this.canLoadMore = this.sections.length < this.allSections.length;
    this.isLoadingMore = false;

    this.cd.markForCheck();
  }

  private cleanupObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  trackBySection(index: number, section: ChangelogSection): string {
    return section.title || `announcement-${index}`;
  }

  formatDate(dateStr: string): string {
    const date = DateTime.fromISO(dateStr);
    const today = DateTime.local();
    return formatRelativeDay(today, date);
  }

}
